import { Api } from 'lib';
import { useCallback, useEffect, useRef, useState } from 'react';
import RefreshButton from './RefreshButton';
import ReferralDrawer from './ReferralDrawer';
import ReferralTable, { ORIGIN_LABELS } from './ReferralTable';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const fmtNumber = (n) => (n || 0).toLocaleString();

const rangeParams = (params, teamId, fromDate, toDate) => {
  if (teamId) params.set('teamId', teamId);
  if (fromDate) params.set('from', new Date(fromDate).toISOString());
  if (toDate) params.set('to', new Date(`${toDate}T23:59:59.999`).toISOString());
};

/**
 * Referral tracker for the Insights page. Reads the referral collections through its own
 * endpoints, not the InsightEvent pipeline, so figures always equal the stored referrals.
 * Uses the page's team and date filters; the module filter does not apply.
 */
const ReferralSection = ({ teamId, fromDate, toDate }) => {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [list, setList] = useState({ rows: [], total: 0 });
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [origin, setOrigin] = useState('');
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailReq = useRef(0);
  // Bumped by the refresh button; both fetch effects depend on it, so it refetches with the
  // current filters and page.
  const [refreshKey, setRefreshKey] = useState(0);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(0);
  }, [teamId, fromDate, toDate, origin, status, search]);

  useEffect(() => {
    let stale = false;
    const params = new URLSearchParams();
    rangeParams(params, teamId, fromDate, toDate);
    const qs = params.toString();
    setSummaryLoading(true);
    Api.get(`/admin/insights/referralSummary${qs ? `?${qs}` : ''}`, null, { silent: true }).then(
      (res) => {
        if (stale) return;
        setSummary(res?.data || null);
        setSummaryLoading(false);
      }
    );
    return () => {
      stale = true;
    };
  }, [teamId, fromDate, toDate, refreshKey]);

  useEffect(() => {
    let stale = false;
    const params = new URLSearchParams();
    rangeParams(params, teamId, fromDate, toDate);
    if (origin) params.set('origin', origin);
    if (status) params.set('status', status);
    if (search) params.set('search', search);
    params.set('limit', PAGE_SIZE);
    params.set('offset', page * PAGE_SIZE);
    setListLoading(true);
    Api.get(`/admin/insights/referralList?${params.toString()}`, null, { silent: true }).then(
      (res) => {
        if (stale) return;
        setList(res?.data || { rows: [], total: 0 });
        setListLoading(false);
      }
    );
    return () => {
      stale = true;
    };
  }, [teamId, fromDate, toDate, origin, status, search, page, refreshKey]);

  const handleRowClick = useCallback(async (row) => {
    const req = ++detailReq.current;
    setSelected(row);
    setDetail(null);
    setDetailLoading(true);
    const res = await Api.get(
      `/admin/insights/referralDetail?module=${row.module}&id=${row.id}`,
      null,
      { silent: true }
    );
    if (req !== detailReq.current) return; // a newer row was clicked meanwhile
    setDetail(res?.data || null);
    setDetailLoading(false);
  }, []);

  // Hide the legacy row while it is empty so a clean install shows no dead row.
  const rows = (summary?.byOrigin || []).filter((r) => r.origin !== 'unknown' || r.count > 0);
  // Incomplete = every referral not yet completed, so completed + incomplete always equals total.
  const incompleteOf = (r) => Math.max(0, (r?.count || 0) - (r?.completed || 0));
  const incompleteTotal = Math.max(0, (summary?.total || 0) - (summary?.completed || 0));

  return (
    <div className="mt-6 w-full space-y-4">
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[#E2E8F0] bg-[#F8FAFC] flex flex-wrap items-start gap-x-8 gap-y-2">
          <div className="mr-auto">
            <p className="text-[13px] font-semibold text-[#0F172A]">Referrals by origin</p>
            <p className="text-[11px] text-[#94A3B8]">
              FanCam and Rate It invites, by where the link was shared from. Uses the team and date
              filters, not the module filter.
            </p>
          </div>
          {!summaryLoading && (
            <div className="flex gap-6 text-right">
              <div>
                <p className="text-[18px] font-semibold text-[#0F172A] leading-tight">
                  {fmtNumber(summary?.total)}
                </p>
                <p className="text-[11px] text-[#94A3B8]">Total</p>
              </div>
              <div>
                <p
                  className={`text-[18px] font-semibold leading-tight ${
                    summary?.completed ? 'text-[#3B5BDB]' : 'text-[#0F172A]'
                  }`}
                >
                  {fmtNumber(summary?.completed)}
                </p>
                <p className="text-[11px] text-[#94A3B8]">Completed</p>
              </div>
              <div>
                <p
                  className={`text-[18px] font-semibold leading-tight ${
                    incompleteTotal ? 'text-[#F59E0B]' : 'text-[#0F172A]'
                  }`}
                >
                  {fmtNumber(incompleteTotal)}
                </p>
                <p className="text-[11px] text-[#94A3B8]">In progress</p>
              </div>
            </div>
          )}
          <RefreshButton
            onClick={() => setRefreshKey((k) => k + 1)}
            refreshing={summaryLoading || listLoading}
          />
        </div>

        {summaryLoading ? (
          <div className="p-5 space-y-3 animate-pulse">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-6 w-full bg-[#F1F5F9] rounded" />
            ))}
          </div>
        ) : (
          <div className="py-2">
            {rows.map((row) => (
              <div key={row.origin} className="flex items-center gap-3 px-5 py-2.5">
                <span className="shrink-0 w-[170px] text-[13px] font-medium text-[#334155] truncate">
                  {ORIGIN_LABELS[row.origin] || row.origin}
                </span>
                <span className="flex-1 h-1.5 overflow-hidden">
                  {/* Stacked: every bar is full width, split by the row's completed and incomplete share. */}
                  <span
                    className="flex h-full rounded-full overflow-hidden"
                    style={{ width: row.count ? '100%' : 0 }}
                  >
                    <span
                      className="h-full bg-[#3B5BDB]"
                      title={`Completed: ${fmtNumber(row.completed)}`}
                      style={{ width: `${(row.completed / row.count) * 100}%` }}
                    />
                    <span
                      className="h-full bg-[#F59E0B]"
                      title={`In progress:${fmtNumber(incompleteOf(row))}`}
                      style={{ width: `${(incompleteOf(row) / row.count) * 100}%` }}
                    />
                  </span>
                </span>
                <span className="shrink-0 w-[60px] text-right text-[13px] font-semibold text-[#0F172A]">
                  {fmtNumber(row.count)}
                </span>
                <span className="shrink-0 w-[200px] text-right text-[11px] text-[#94A3B8]">
                  <span className="text-[#3B5BDB]">{fmtNumber(row.completed)}</span> completed ·{' '}
                  <span className="text-[#F59E0B]">{fmtNumber(incompleteOf(row))}</span> in progress
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReferralTable
        rows={list.rows}
        total={list.total}
        loading={listLoading}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        origin={origin}
        onOriginChange={setOrigin}
        status={status}
        onStatusChange={setStatus}
        search={searchInput}
        onSearchChange={setSearchInput}
        onRowClick={handleRowClick}
      />

      {selected && (
        <ReferralDrawer
          row={selected}
          detail={detail}
          loading={detailLoading}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
};

export default ReferralSection;
